import type { Move } from '../../../schemas/move';

/**
 * Mosse iniziali dello Sciamano (manuale V1, rr. 5514-5555).
 *
 * - **3 mosse automatiche** (`type: 'starting'`): *Portavoce dell'Aldilà*,
 *   *Aiuto dall'Altro Mondo*, *Onorevoli Padri*.
 * - **3 mosse di scelta** (`pickCount: 1`): *Medium*, *Auspicio*,
 *   *Radici Profonde*. Manuale r. 5541: «*Inoltre, scegli una delle seguenti
 *   mosse:*».
 *
 * **Pattern critici**:
 *
 * - ***Aiuto dall'Altro Mondo*** (mossa-tiro+CAR a prese): pattern noto
 *   coerente con *Meditazione* del Monaco e *Aura* del Paladino. I 3 effetti
 *   delle prese (terrore / accecare-assordare-rendere-muto / debolezza)
 *   sono runtime, restano in description.
 * - ***Portavoce dell'Aldilà*** è il **target di forward-reference cross-class**
 *   già presente in repo: il Druido cita questo ID in `prerequisites.anyOf`
 *   in 2 mosse (`colui-che-parla-alle-cose`, `spiriti-guardiani`). Ora che lo
 *   Sciamano è trascritto, l'ID esiste localmente — il `knownExternalIds` nei
 *   test del Druido perde questa entry (D4).
 * - ***Medium*** richiede *Onorevoli Padri* (auto), prereq sempre soddisfatto
 *   per il PG L1.
 */

const portavoceDellAldila: Move = {
  id: 'sciamano.portavoce-dell-aldila',
  name: "Portavoce dell'Aldilà",
  type: 'starting',
  classId: 'sciamano',
  trigger: 'Sempre (effetto continuo) e quando ti *accampi* con il materiale necessario',
  description:
    "Puoi vedere, capire ed essere compreso dagli spiriti dei defunti, che siano corporei o meno. Inoltre, quando **ti *accampi* e hai con te il materiale necessario** (concordalo con il GM), ottieni 1 utilizzo di Farina d'ossa, utile per placare uno spirito adirato (peso 1, massimo 3 utilizzi).",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const aiutoDallAltroMondo: Move = {
  id: 'sciamano.aiuto-dall-altro-mondo',
  name: "Aiuto dall'Altro Mondo",
  type: 'starting',
  classId: 'sciamano',
  trigger: "Quando ti prendi qualche momento per attingere al potere dell'aldilà",
  description:
    "Perdi tutte le prese residue, subisci 2 danni (*ignora armatura*) e tira+**CAR**. Con un 10+, prendi 3. Con 7-9, puoi prendere 2 e subire -1 continuato a tutti i tiri di *Aiuto dall'Altro Mondo* fino alla prossima alba, oppure prendere solo 1. Con 6-, prendi lo stesso una presa, ma gli spiriti sono adirati e ne subirai le conseguenze (a discrezione del GM).\n\nSpendi 1 presa per ottenere uno dei seguenti effetti su una creatura vivente che puoi vedere:\n\n- riempirla di un sovrannaturale senso di terrore, facendola tremare o sussultare per alcuni istanti;\n- accecarla, assordarla o renderla muta per qualche momento;\n- riempirla di un senso di debolezza sovrannaturale, facendola inciampare o lasciar cadere ciò che sta trasportando.\n\nOgni volta che spendi prese di questa mossa, descrivi quale aspetto dell'aldilà si manifesta in questo mondo (bruma di sangue che filtra dal suolo, ululati dei dannati che tormentano il bersaglio, ecc.).",
  roll: {
    stat: 'carisma',
    on10plus: { text: 'Prendi 3.' },
    on7to9: {
      text: "Prendi 2 e subisci -1 continuato a tutti i tiri di *Aiuto dall'Altro Mondo* fino alla prossima alba, oppure prendi solo 1.",
    },
    on6minus: {
      text: 'Prendi 1 presa, ma gli spiriti sono adirati e ne subirai le conseguenze (a discrezione del GM).',
    },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const onorevoliPadri: Move = {
  id: 'sciamano.onorevoli-padri',
  name: 'Onorevoli Padri',
  type: 'starting',
  classId: 'sciamano',
  trigger: 'Quando nomini un antenato e invochi il suo fantasma in tuo aiuto',
  description:
    "Descrivilo e tira+**CAR**. Con 10+, riesci a entrare in contatto con chi volevi invocare e questi ti aiuterà volentieri (supponendo che ne sia ragionevolmente in grado). Con 7-9, il GM sceglie uno:\n\n- non vuole aiutarti;\n- decide di starsene tra i piedi e farti la paternale sui tuoi fallimenti per un po' di tempo;\n- comunichi con uno spirito completamente diverso.",
  roll: {
    stat: 'carisma',
    on10plus: {
      text: "Entri in contatto con l'antenato voluto e ti aiuterà volentieri (se ne è in grado).",
    },
    on7to9: { text: 'Il GM sceglie 1 opzione dalla lista.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const medium: Move = {
  id: 'sciamano.medium',
  name: 'Medium',
  type: 'starting',
  classId: 'sciamano',
  trigger: 'Quando invochi un fantasma con *Onorevoli Padri*',
  description:
    "Puoi anche nominare un'anima defunta che non sia un tuo antenato. Per farlo, però, devi conoscere il suo vero nome, e possedere qualcosa cui essa è legata o trovarti in un luogo per lei significativo.",
  tags: [],
  // Manuale r. 5545: "Richiede: *Onorevoli Padri*". Per il PG L1 è sempre
  // soddisfatto perché *Onorevoli Padri* è iniziale auto.
  prerequisites: ['sciamano.onorevoli-padri'],
  replaces: [],
};

const auspicio: Move = {
  id: 'sciamano.auspicio',
  name: 'Auspicio',
  type: 'starting',
  classId: 'sciamano',
  trigger:
    'Quando trascorri alcuni minuti a interpretare i segni che gli spiriti ti mostrano in merito a qualcosa che vorresti fare',
  description:
    "Tira+**SAG**. Con 7-9, scopri se gli auspici sono favorevoli o contrari. Con 10+, ottieni anche un +1 al prossimo tiro se segui l'indicazione ricevuta. Con 6-, i segni sono oscuri e subisci un -1 al prossimo tiro.",
  roll: {
    stat: 'saggezza',
    on10plus: { text: 'Scopri se gli auspici sono favorevoli o contrari + +1 al prossimo tiro.' },
    on7to9: { text: 'Scopri se gli auspici sono favorevoli o contrari.' },
    on6minus: { text: 'I segni sono oscuri: -1 al prossimo tiro.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const radiciProfonde: Move = {
  id: 'sciamano.radici-profonde',
  name: 'Radici Profonde',
  type: 'starting',
  classId: 'sciamano',
  trigger: 'Quando *declami conoscenze* riguardo al tuo popolo',
  description: 'Puoi tirare+**SAG** invece che +**INT**.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

/**
 * Mosse iniziali AUTOMATICHE (3): prese tutte al livello 1 senza scelta.
 */
export const sciamanoStartingAutoMoves: readonly Move[] = [
  portavoceDellAldila,
  aiutoDallAltroMondo,
  onorevoliPadri,
] as const;

/**
 * Mosse iniziali di SCELTA (3): il giocatore ne sceglie UNA al livello 1
 * (`pickCount: 1`). *Medium* / *Auspicio* / *Radici Profonde*.
 */
export const sciamanoStartingChoiceMoves: readonly Move[] = [
  medium,
  auspicio,
  radiciProfonde,
] as const;

/** Tutte le mosse iniziali (auto + scelta). */
export const sciamanoInitialMoves: readonly Move[] = [
  ...sciamanoStartingAutoMoves,
  ...sciamanoStartingChoiceMoves,
];
